import { type StateCreator } from "zustand";

export const RECENT_PAGES_CAP = 15;

export interface RecentPageEntry {
  /** Navigation target: pathname + search exactly as visited. */
  path: string;
  /** Dedupe key: pathname only. */
  pathname: string;
  /** Human label. Best-effort at record time; upgraded by detail pages. */
  label: string;
  /** Section sublabel resolved at record time (nav-group title, "" if none). */
  section: string;
  /** Epoch ms supplied by the caller (slice does no clock reads). */
  visitedAt: number;
}

export interface RecentPagesState {
  recentPages: RecentPageEntry[];
}

export interface RecentPagesActions {
  /** Move-to-top dedupe by `pathname`: an existing entry is replaced in
   *  place at index 0 with the new path/label/section/visitedAt; otherwise
   *  prepend and truncate to RECENT_PAGES_CAP. */
  recordRecentPage: (entry: RecentPageEntry) => void;
  /** Update `label` of the entry with this pathname, if present. No reorder,
   *  no timestamp change, no-op when absent or label is empty/unchanged. */
  upgradeRecentPageLabel: (pathname: string, label: string) => void;
  clearRecentPages: () => void;
}

export type RecentPagesSlice = RecentPagesState & RecentPagesActions;

/**
 * A visitor's navigation trail. The slice itself holds no storage: durability
 * is the consuming app's call, because the trail is scoped to one org context
 * and one signed-in session — a trail rehydrated into the wrong context is a
 * list of dead links. Web gives it per-tab sessionStorage through its own sync
 * module and clears it on an org switch or sign-out; it stays out of the
 * localStorage persist allow-list.
 */
export const createRecentPagesSlice: StateCreator<RecentPagesSlice, [], [], RecentPagesSlice> = (
  set,
) => ({
  recentPages: [],
  recordRecentPage: (entry) =>
    set((state) => ({
      recentPages: [
        entry,
        ...state.recentPages.filter((page) => page.pathname !== entry.pathname),
      ].slice(0, RECENT_PAGES_CAP),
    })),
  upgradeRecentPageLabel: (pathname, label) =>
    set((state) => {
      if (!label) return state;
      const index = state.recentPages.findIndex((page) => page.pathname === pathname);
      const current = state.recentPages[index];
      if (!current || current.label === label) return state;
      const recentPages = [...state.recentPages];
      recentPages[index] = { ...current, label };
      return { recentPages };
    }),
  clearRecentPages: () => set({ recentPages: [] }),
});
